// Custom.js

/* ==========================================================================
   Cart drawer upgrade button
   Added 2026-08-31

   snippets/modal_cart-drawer.line-items-with-upsells.liquid calls:

     _product.getHydratedProductData(
       product?.metafields?.smart?.upgrade,   // expected to be a handle STRING
       product?.upgrade_id
     )

   snippets/_json.metafield.liquid serialises a product_reference metafield as
   an OBJECT - { id, handle } - so an object arrives where a string is
   expected. Two symptoms followed:

     1. The wrong product's name, or "Upgrade to the undefined".
        _products[handle] is keyed by String(object) === "[object Object]", so
        every product's upgrade lookup shared ONE cache slot. Whichever
        upgrade resolved first won, and when that slot held an empty object it
        was still truthy, so the template rendered ${undefined}.

     2. A bare "Upgrade" on mobile.
        With the cache key broken, the only thing left that could resolve the
        product was the theme's /recommendations/products fetch. That response
        is ~3 MB on this store, so on a phone it often had not landed by the
        time the drawer was read.

   Fix: when an OBJECT is passed as the handle - which only the upgrade call
   site does - resolve through the small, market and locale aware
   /products/{handle}.js endpoint, keyed by the real handle. Callers that pass
   a handle string keep the theme's original path untouched, which matters
   because /products/x.js does not expose inventory_management or
   inventory_quantity and swapping it in globally would break the drawer's
   quantity cap.

   Prewarming: the button's label starts on its hard-coded "Upgrade" fallback
   and swaps once the lookup resolves. To keep that swap from being visible,
   resolved upgrade products are held in localStorage per market and seeded
   back synchronously on the next page load, the current product's upgrade is
   prefetched as soon as the page has it, and any line item the theme hydrates
   prefetches its own upgrade in the background.

   The underlying defect is the call site in the vendor snippet and is worth
   reporting to the theme vendor. It is patched here so a theme update cannot
   silently revert it. The snippet-level equivalent would be passing
   product?.metafields?.smart?.upgrade?.handle.
   ========================================================================== */

(function () {
  var GIVE_UP_AFTER = 15000;
  var STORE_KEY = "_upgrade_products_v1";
  var STORE_TTL = 1000 * 60 * 60 * 12;
  var startedAt = Date.now();

  var pending = {}; // handle -> Promise
  var resolved = {}; // handle -> product, readable synchronously

  function routeRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  // Titles and variants are market and locale specific, so cached entries are
  // only reused within the same market.
  function marketKey() {
    var shopify = window.Shopify || {};
    return [
      routeRoot(),
      shopify.country || "",
      shopify.locale || document.documentElement.lang || ""
    ].join("|");
  }

  function isUsable(product) {
    return !!(product && product.title && product.selected_or_first_available_variant_id);
  }

  function readStore() {
    try {
      var stored = JSON.parse(localStorage.getItem(STORE_KEY) || "null");
      if (!stored || stored.market !== marketKey()) return {};
      if (Date.now() - (stored.savedAt || 0) > STORE_TTL) return {};
      return stored.products || {};
    } catch (error) {
      return {};
    }
  }

  function writeStore() {
    try {
      localStorage.setItem(
        STORE_KEY,
        JSON.stringify({ market: marketKey(), savedAt: Date.now(), products: resolved })
      );
    } catch (error) {
      /* private mode or quota - the fetch path still works */
    }
  }

  (function seedFromStore() {
    var stored = readStore();
    Object.keys(stored).forEach(function (handle) {
      if (!isUsable(stored[handle])) return;
      resolved[handle] = stored[handle];
      pending[handle] = Promise.resolve(stored[handle]);
    });
  })();

  function fetchLiteProduct(handle) {
    if (pending[handle]) return pending[handle];

    pending[handle] = fetch(routeRoot() + "products/" + handle + ".js")
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (raw) {
        if (!raw || !raw.title) return null;

        var variants = raw.variants || [];
        var firstAvailable = variants.filter(function (variant) {
          return variant.available;
        })[0];
        var variant = firstAvailable || variants[0] || null;

        var lite = {
          id: raw.id,
          handle: raw.handle,
          title: raw.title,
          url: raw.url,
          available: raw.available,
          price: raw.price,
          compare_at_price: raw.compare_at_price,
          featured_image: raw.featured_image,
          variants: variants,
          selected_or_first_available_variant_id: variant ? variant.id : null
        };

        resolved[handle] = lite;
        writeStore();
        return lite;
      })
      .catch(function () {
        return null;
      });

    return pending[handle];
  }

  function prewarmFrom(product) {
    var reference =
      product && product.metafields && product.metafields.smart && product.metafields.smart.upgrade;
    var handle = reference && reference.handle;

    if (handle && !pending[handle]) fetchLiteProduct(handle);
  }

  function prewarmCart() {
    fetch(routeRoot() + "cart.js")
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        if (!cart || !cart.items) return;
        cart.items.forEach(function (item) {
          var known = window._products && window._products[item.handle];
          if (known) prewarmFrom(known);
        });
      })
      .catch(function () {});
  }

  function applyPatch() {
    var api = window._product;

    if (!api || typeof api.getHydratedProductData !== "function") return false;
    if (api.__upgradeHandleFix) return true;

    var original = api.getHydratedProductData;

    api.getHydratedProductData = async function (handle, productId) {
      // Every normal caller passes a handle string - leave that path alone,
      // but use what it returns to prefetch that product's upgrade.
      if (!handle || typeof handle !== "object") {
        var result = original.call(api, handle, productId);

        if (result && typeof result.then === "function") {
          result
            .then(function (data) {
              prewarmFrom(data);
            })
            .catch(function () {});
        }

        return result;
      }

      var key = handle.handle;
      var id = productId || handle.id || null;

      if (!key) return original.call(api, handle, id);

      // Seeded from localStorage on this page load - no request, no flash.
      if (isUsable(resolved[key])) return resolved[key];

      // Anything the theme has already hydrated properly wins.
      var cached = window._products && window._products[key];
      if (isUsable(cached)) return cached;

      var lite = await fetchLiteProduct(key);
      if (isUsable(lite)) return lite;

      try {
        var full = await original.call(api, key, id);
        if (full && full.title) return full;
      } catch (error) {
        /* fall through - the button keeps its "Upgrade" label */
      }

      return null;
    };

    api.__upgradeHandleFix = true;

    prewarmFrom(window._product_card_product);
    setTimeout(prewarmCart, 0);

    return true;
  }

  if (!applyPatch()) {
    var poll = setInterval(function () {
      if (applyPatch() || Date.now() - startedAt > GIVE_UP_AFTER) clearInterval(poll);
    }, 50);
  }
})();
